import { useState } from 'react'
import { Element } from '@phrasee/phrasee-typings'
import cx from 'classnames'

import ConfirmationModal from 'common/components/confirmationModal/ConfirmationModal'
import MenuButton, { MenuDivider, MenuItem } from 'common/components/menuButton'
import { useAppSelector } from 'common/hooks/redux'
import { Bin, Edit, MoreOptionsVertical } from 'common/icons'
import useDeleteElementMutation from 'features/unifiedFlow/api/mutations/useDeleteElementMutation'

import { getElementHasVariants } from '../helpers'
import { useContent } from '../hooks'

import ElementRenameModal from './ElementRenameModal'

type Props = {
  element?: Element
  isSelected?: boolean
  isMenuOpen?: boolean
  isRemoveOptionShown?: boolean
  isTransitionActive?: boolean
  onMenuChange?: (changeEvent: { open: boolean }) => void
}

const ElementVerticalDotMenu = ({
  isSelected,
  element,
  isMenuOpen,
  isRemoveOptionShown = true,
  isTransitionActive = false,
  onMenuChange,
}: Props) => {
  const [showRemoveModal, setShowRemoveModal] = useState(false)
  const [showRenameModal, setShowRenameModal] = useState(false)
  const { content } = useContent()

  const elements = content?.elements || []
  const deleteElementMutation = useDeleteElementMutation()

  const accountId = useAppSelector((state) => state.authStates.accountId)
  const activeContentId = useAppSelector(
    (state) => state.unifiedFlow.activeContentId
  )

  const elementId = element?.element_id

  const handleRename = () => {
    setShowRenameModal(true)
  }

  const handleRemove = (event) => {
    event.stopPropagation()
    if (elementId && activeContentId && elements?.length > 1) {
      if (getElementHasVariants(element) && !showRemoveModal) {
        setShowRemoveModal(true)
        return
      }
      deleteElementMutation.mutate({
        elementId,
        accountId,
        contentId: activeContentId,
      })
    }
    setShowRemoveModal(false)
  }

  return (
    <>
      <MenuButton
        portal
        data-testid="more-options"
        onMenuChange={onMenuChange}
        buttonProps={{
          'aria-label': 'More options',
          onClick: (e) => e.stopPropagation(),
          className: cx({
            'w-0 transition-width duration-200 group-hover:w-6':
              isTransitionActive,
            'invisible group-hover:visible': !isMenuOpen,
            'visible w-6': isMenuOpen,
          }),
          children: (
            <MoreOptionsVertical
              isDefaultColor={false}
              size={6}
              className="hover:text-base-700 outline-none text-base-700"
            />
          ),
        }}
        options={
          <>
            <MenuItem key="1" onClick={handleRename} aria-label="Rename">
              <Edit isDefaultColor={false} className="mr-4 text-coolGray-400" />
              Rename
            </MenuItem>
            {isRemoveOptionShown && (
              <>
                <MenuDivider />
                <MenuItem
                  key="3"
                  onClick={(e) => handleRemove(e.syntheticEvent)}
                  aria-label="Delete"
                >
                  <Bin
                    isDefaultColor={false}
                    className="mr-4 text-coolGray-400"
                  />
                  Remove
                </MenuItem>
              </>
            )}
          </>
        }
      />
      <ConfirmationModal
        data-cy="remove-element-modal"
        data-testid="remove-element-modal"
        title="Remove content"
        confirmationText="Are you sure you want to permanently remove this content? Once it's gone, it's gone!"
        confirmButtonText="Remove"
        cancelButtonText="Cancel"
        open={showRemoveModal}
        hasConfirmationSlider={true}
        onConfirm={handleRemove}
        onCancel={() => setShowRemoveModal(false)}
      />
      {showRenameModal && element && (
        <ElementRenameModal
          element={element}
          onClose={() => setShowRenameModal(false)}
        />
      )}
    </>
  )
}

export default ElementVerticalDotMenu
